import React, { FC, ReactNode, Suspense, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter, Route, Switch, useLocation, Redirect } from 'react-router-dom'
import { CompatRouter } from 'react-router-dom-v5-compat'

import { Capacitor } from '@capacitor/core'
import { Network } from '@capacitor/network'
import { Device } from '@capacitor/device'

import { AppUrlListener, PrivateRoute, TopBar } from 'components'
import Footer from 'components/Footer'
import { useModal, useFetchMerinioInitialData } from 'components/Hooks'
import ModalRoot from 'components/Modals/ModalRoot'
import { Loading } from 'components/UI'
import { setPlateform } from 'store/actions/applicationInfo'
import { refreshStaleResources } from 'store/actions/resources'
import lazyImportRetry from 'services/lazyImportRetry'

import NoAuth, { noAuthPaths } from 'NoAuth'
import Notifications from 'components/Notifications'
import ModalErrorBoundary from 'components/ModalErrorBoundary'
import { currentLang, setLanguage } from 'translations'
import { setLoggedOut } from './store/actions/auth'
import { store } from 'store/reducers'
import { hasAccessToThis } from 'services/Utilities'
import useRedirectTo from 'components/Hooks/useRedirectTo'
import { MerinioChatContainer } from 'features'

const Administration = React.lazy(() => lazyImportRetry(() => import('Administration')))
const Employee = React.lazy(() => lazyImportRetry(() => import('Employee')))
const Manager = React.lazy(() => lazyImportRetry(() => import('Manager')))
const Onboarding = React.lazy(() => lazyImportRetry(() => import('Onboarding')))
const PunchTerminal = React.lazy(() => lazyImportRetry(() => import('PunchTerminal')))

const App: FC = () => {
  useFetchMerinioInitialData()

  const redirectTo = useRedirectTo()
  const dispatch = useDispatch()
  const [PreventLeavingModal, toggleIsLeaving] = useModal({ shouldCloseOnOverlayClick: false, basicText: true })

  const auth = useSelector((state: MStore.RootState) => state.auth)
  const punchTerminal = useSelector((state: MStore.RootState) => state.punchTerminal)
  const resourcesAllLoaded = useSelector((state: MStore.RootState) => state.resources?.resourcesAllLoaded)
  const settingsAllLoaded = useSelector((state: MStore.RootState) => state.settings?.settingsAllLoaded)
  const userInfo = useSelector((state: MStore.RootState) => state.users?.userInfo)

  const [modalData, setModalData] = useState<{ callback?: (ok: boolean) => void; message?: string }>({})
  const [noInternetAccess, setNoInternetAccess] = useState(false)

  const appLoaded = Boolean(auth.isAuthenticated && resourcesAllLoaded && userInfo && settingsAllLoaded)
  const isAuthenticated = auth.isAuthenticated
  const isPunchToken = punchTerminal.isPunchToken
  const restrictToPunchTerminal = punchTerminal.restrictToPunchTerminal
  const isAManager = hasAccessToThis('managerPanel')

  useEffect(() => {
    Network.addListener('networkStatusChange', status => setNoInternetAccess(!status.connected))

    const getPlatform = async () => {
      const { platform } = await Device.getInfo()
      dispatch(setPlateform(platform))
    }

    getPlatform()

    return () => {
      Network.removeAllListeners()
    }
  }, [dispatch])

  // we need this to handle the case when the user has multiple tabs open
  useEffect(() => {
    const handleStorageChange = () => {
      //https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
      const token = localStorage.getItem('token')
      if (isAuthenticated && !token) {
        setLoggedOut(store)
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => window.removeEventListener('storage', handleStorageChange)
  }, [isAuthenticated])

  useEffect(() => {
    if (userInfo?.language && currentLang() !== userInfo.language) setLanguage(userInfo.language)
  }, [userInfo?.language])

  if (noInternetAccess) return <Loading noInternet />

  const waitToGetTheRightPath = () => {
    const myDefaultPath = isAManager ? '/manage/dashboard' : '/my/home'
    if (appLoaded && isAuthenticated) return <Redirect to={redirectTo ?? myDefaultPath} />
    return null
  }
  const showChat = isAuthenticated && !window.Cypress && !isPunchToken

  return (
    <BrowserRouter
      // Code to put a custom Prevent modal (instead of window.confirm)
      getUserConfirmation={(message, callback) => {
        toggleIsLeaving({})
        setModalData({ callback, message })
      }}
    >
      <CompatRouter>
        <MainContainer appLoaded={appLoaded} isAuthenticated={isAuthenticated} isPunchToken={isPunchToken} restrictToPunchTerminal={restrictToPunchTerminal}>
          <Suspense fallback={<Loading />}>
            <Switch>
              {isAuthenticated && redirectTo ? <Redirect from="*" to={redirectTo} /> : null}
              <PrivateRoute path="/my" component={Employee} />
              <PrivateRoute path="/admin" component={Administration} />
              <PrivateRoute path="/manage" component={Manager} />
              <PrivateRoute path="/punch" component={PunchTerminal} />
              <Route path={noAuthPaths} component={NoAuth} />
              {/* Onboarding should use private route since the user is not logged in initially. */}
              <Route path="/onboarding" component={Onboarding} />
              {/* Redirects user to correct path on refresh */}
              <PrivateRoute path="*" component={waitToGetTheRightPath} />
            </Switch>
          </Suspense>
        </MainContainer>
        <AppUrlListener />

        {/* Modal to replace the onLeave window.confirm */}
        <PreventLeavingModal title={modalData.message} primaryAction={modalData.callback} primaryActionText="LEAVE" />
        <ModalErrorBoundary>
          <ModalRoot />
        </ModalErrorBoundary>
        {showChat ? <MerinioChatContainer token={auth.decodedToken?.chatSdkToken} /> : null}
      </CompatRouter>
    </BrowserRouter>
  )
}

type MainContainerProps = {
  appLoaded: boolean
  isAuthenticated: boolean
  isPunchToken: boolean
  restrictToPunchTerminal: boolean
  children: ReactNode
}

const MainContainer: FC<MainContainerProps> = ({ appLoaded, children, isPunchToken, restrictToPunchTerminal }) => {
  const location = useLocation()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(refreshStaleResources())
  }, [location, dispatch])
  // remove when no longer needed , we redirecting from hash router to brwoserRouter
  useEffect(() => {
    const historyLocationHash = window.location.hash

    if (historyLocationHash) {
      const urlWitoutHash = historyLocationHash.substring(1)
      window.location.replace(urlWitoutHash)
    }
  }, [])

  const { pathname } = useLocation()
  const isOnboarding = pathname.includes('/onboarding')
  const isPunchLocation = pathname.includes('/punchLocation')

  const addMobileAppStyling = Capacitor.isNativePlatform() ? ' is-mobile-app' : ''

  return (
    <div className={`d-flex flex-column app-container${addMobileAppStyling}`}>
      <Notifications />
      {appLoaded && !isOnboarding && !isPunchLocation && !isPunchToken && !restrictToPunchTerminal ? <TopBar /> : null}
      <div className={`d-flex flex-column flex-even app-content container-fluid p-0`}>
        <div className="flex-even">{children}</div>
        {appLoaded && !isPunchLocation && !isPunchToken && !restrictToPunchTerminal ? <Footer /> : null}
      </div>
    </div>
  )
}

export default App
